// src/app/modules/Dashboard/components/SubscriptionBanner.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, X, Zap, CreditCard, AlertCircle, XCircle } from "lucide-react";
import { useSubscriptionStatus } from "../../../../hooks/useSubscriptionStatus";
import './subscription.scss';


export function SubscriptionBanner() {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const { isTrial, isPastDue, isSuspended, isCancelled, needsPayment, trialDaysLeft, isLoaded, hasAccess } = useSubscriptionStatus();

  // Nothing to show
  if (!isLoaded || dismissed) return null;
  if (hasAccess && !isTrial) return null; // active paid plan, all good

  const goToBilling = () => navigate("/settings?tab=billing");
  const viewPlan = () => navigate("/settings?tab=billing&action=subscribe");

  // ── Trial ending soon (≤ 7 days) ─────────────────────────────────────────
  if (isTrial && trialDaysLeft !== null && trialDaysLeft <= 7) {
    return (
      <div className="sub-banner sub-banner--warning">
        <Zap size={16} />
        <span>
          Your free trial ends in <strong>{trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""}</strong>.
          Subscribe now to keep access to all features.
        </span>
        <button className="sub-banner__cta" onClick={goToBilling}>
          Subscribe Now
        </button>
        <button className="sub-banner__dismiss" onClick={() => setDismissed(true)}>
          <X size={14} />
        </button>
      </div>
    );
  }

  // ── Trial active (> 7 days) — subtle nudge ────────────────────────────────
  if (isTrial && trialDaysLeft !== null && trialDaysLeft > 7) {
    return (
      <div className="sub-banner sub-banner--info">
        <Zap size={16} />
        <span>
          You're on a free trial — <strong>{trialDaysLeft} days remaining</strong>.
        </span>
        <button className="sub-banner__cta sub-banner__cta--ghost" onClick={viewPlan}>
          View Plans
        </button>
        <button className="sub-banner__dismiss" onClick={() => setDismissed(true)}>
          <X size={14} />
        </button>
      </div>
    );
  }

  // ── Past due ──────────────────────────────────────────────────────────────
  if (isPastDue) {
    return (
      <div className="sub-banner sub-banner--danger">
        <AlertCircle size={16} />
        <span>
          <strong>Payment failed.</strong> Your subscription is past due — update your payment method to avoid suspension.
        </span>
        <button className="sub-banner__cta" onClick={goToBilling}>
          Fix Now
        </button>
      </div>
    );
  }

  // ── Suspended ─────────────────────────────────────────────────────────────
  if (isSuspended) {
    return (
      <div className="sub-banner sub-banner--danger">
        <XCircle size={16} />
        <span>
          <strong>Account suspended.</strong> Your access is restricted — reactivate your subscription to continue.
        </span>
        <button className="sub-banner__cta" onClick={goToBilling}>
          Reactivate
        </button>
      </div>
    );
  }

  // ── No plan / cancelled ───────────────────────────────────────────────────
  if (needsPayment) {
    return (
      <div className="sub-banner sub-banner--warning">
        <CreditCard size={16} />
        <span>
          You don't have an active plan. Some features may be restricted.
        </span>
        <button className="sub-banner__cta" onClick={viewPlan}>
          Choose a Plan
        </button>
        <button className="sub-banner__dismiss" onClick={() => setDismissed(true)}>
          <X size={14} />
        </button>
      </div>
    );
  }

  return null;
}